import { $ti, $tm } from '@/locize';
import { Eventinfo, Station, StationAlias, StationDetails, StationRole, type EventinfoBase } from '@/objects/generated';
import { TimeSpan } from '@/objects/TimeSpan';
import { useUtilStore } from '@/stores';
import { addTimeToDate, formatDateDefault, getDateWithMaxTime, getDateWithMinTime } from '@/utilities/dateutils';

/**
 * Help us create a new StationRole with default values
 * @param race
 * @param station
 * @param event
 * @returns
 */
export const createStationRole = (
    station: Station | StationDetails | null,
    event: EventinfoBase | Eventinfo,
    raceId: number | null = null,
) => {
    return StationRole.FromJsValues({
        EntityState: 'Added',
        ID: useUtilStore().nextFakeId(),
        RaceID: raceId,
        EventID: event.ID,
        StationID: station?.ID ?? 0,
        Name: station?.Name ?? '',
        Sort: 0,
        Km: 0,
        Public: true,
        MinLapTime: TimeSpan.FromSeconds(20),
        // We need to set the start and stop time to the Event.Date 00:00 to 23:59:59
        StartTime: getDateWithMinTime(event?.EventStart ?? event?.Date ?? new Date()),
        StopTime: getDateWithMaxTime(event?.EventEnd ?? event?.Date ?? new Date()),
    });
};

/**
 * Create a new StationAlias with default values based on the event
 * @param event
 * @returns
 */
export const createStationAlias = (event: EventinfoBase | Eventinfo) => {
    return StationAlias.FromJsValues({
        EntityState: 'Added',
        ID: useUtilStore().nextFakeId(),
        ActiveFrom: getDateWithMinTime(event?.EventStart ?? event?.Date ?? new Date()),
        ActiveTo: getDateWithMaxTime(event?.EventEnd ?? event?.Date ?? new Date()),
        ReadsBib: true,
        ReadsChip: true,
    });
};

/**
 * Return a list of errors for this role
 * @param item The role to check
 * @param event
 * @param list
 * @returns
 */
export const getStationRoleErrors = (item: StationRole, event: EventinfoBase, list: StationRole[]): string[] => {
    const errors: string[] = [];

    if (!event || !item) {
        return errors;
    }

    errors.push(...getStartErrors(item, list));
    errors.push(...getNeutralZoneErrors(item, list));
    errors.push(...getInvalidTimeErrors(item, list, event));
    errors.push(...getFinishErrors(item, list));
    return errors;
};

/**
 * Return a list of warnings for this role
 * @param item The role to check
 * @param event
 * @param list
 * @returns
 */
export const getStationRoleWarnings = (item: StationRole, event: EventinfoBase, list: StationRole[]): string[] => {
    const warnings: string[] = [];

    if (!event || !item) {
        return warnings;
    }

    warnings.push(...getDistanceWarnings(item, list));
    warnings.push(...getStartWarnings(item, list));
    return warnings;
};

/**
 * Get the warnings related to distance (Km)
 * @param previousRole
 * @returns
 */
const getDistanceWarnings = (item: StationRole, list: StationRole[]): string[] => {
    const previousRole =
        list.filter((x) => x.RaceID == item.RaceID).findIndex((x) => x.ID == item.ID) > 0
            ? list[list.findIndex((x) => x.ID == item.ID) - 1]
            : null;

    if (!previousRole) return [];

    const warnings: string[] = [];
    if (previousRole.Km != null && item.Km !== null && item.Km < previousRole.Km) {
        warnings.push($ti('stationsetupwizard.kmnotincreasing', { previous: previousRole?.Name, current: item.Name }, true));
    }

    return warnings;
};

/**
 * This will check if the current time from - time to is outside the Event defined times and return an error
 * Or if the the time to is less than the previous time to
 * @param item
 * @param list
 * @param event
 * @returns
 */
const getInvalidTimeErrors = (item: StationRole, list: StationRole[], event: EventinfoBase): string[] => {
    const errors: string[] = [];

    // Check that the stoptime is after the start time
    if (item.StartTime != null && item.StopTime != null && item.StartTime > item.StopTime) {
        errors.push(
            $ti(
                'stationsetupwizard.starttimeafterstoptime',
                {
                    starttime: formatDateDefault(item.StartTime, 'DATETIME'),
                    stoptime: formatDateDefault(item.StopTime, 'DATETIME'),
                },
                true,
            ),
        );
    }

    // this is a bit of a hack since the event.EventEnd is always the time 23:59:00 so we default that one to 23:59:59
    const eventEnd = event?.EventEnd;
    if (eventEnd && eventEnd.getHours() == 23 && eventEnd.getMinutes() == 59 && eventEnd.getSeconds() == 0) {
        eventEnd.setSeconds(59);
        eventEnd.setMilliseconds(999);
    }

    // TODO: We should probably check if the current item is active in the same or a later period then the previous one
    // const previousRole =
    // list.filter((x) => x.RaceID == item.RaceID).findIndex((x) => x.ID == item.ID) > 0
    //     ? list[list.findIndex((x) => x.ID == item.ID) - 1]
    //     : null;
    // if (previousRole && item.StartTime != null && previousRole.StopTime != null && item.StartTime < previousRole.StopTime) {
    //     errors.push(
    //         $ti(
    //             'stationsetupwizard.timefrombeforeprevioustimeto',
    //             {
    //                 previousrole: previousRole?.Name,
    //                 previoustime: formatDateDefault(previousRole.StopTime, 'DATETIME'),
    //                 currenttime: formatDateDefault(item.StartTime, 'DATETIME'),
    //             },
    //             true,
    //         ),
    //     );
    // }

    // We need to see if the item StartTime and/or EndTime is outside the EventStart and EventEnd
    if (
        (item.StartTime != null && event.EventEnd != null && item.StartTime > event.EventEnd) ||
        (item.StopTime != null && event.EventStart != null && item.StopTime < event.EventStart)
    ) {
        errors.push(
            $ti(
                'stationsetupwizard.roleendtimeoutsideeventstart',
                {
                    eventstart: formatDateDefault(event.EventStart, 'DATETIME'),
                    itemend: formatDateDefault(item.StopTime, 'DATETIME'),
                    itemstart: formatDateDefault(item.StartTime, 'DATETIME'),
                    eventend: formatDateDefault(eventEnd, 'DATETIME'),
                },
                true,
            ),
        );
    }

    return errors;
};

/**
 * We need to make sure that neutral zone ins have a neutral zone out immediately after, and that neutral zone outs have a neutral zone in immediately before
 * @param list
 * @returns
 */
const getNeutralZoneErrors = (item: StationRole, list: StationRole[]): string[] => {
    const errors: string[] = [];

    const thisRaceRoles = list.filter((x) => x.RaceID == item.RaceID);

    // If this is a neutral zone in we need to make sure that the next role is a zone out, if it is a zone out, we need to make sure that the previous is a zone in
    if (item.IsNeutralZoneIn) {
        const idx = thisRaceRoles.findIndex((x) => x.ID == item.ID);
        if (idx < thisRaceRoles.length - 1) {
            const next = thisRaceRoles[idx + 1];
            if (!next.IsNeutralZoneOut) {
                errors.push($ti('stationsetupwizard.neutralzoneinwithoutout', { current: item?.Name, next: next?.Name }, true));
            }
        } else {
            errors.push($ti('stationsetupwizard.neutralzoneinwithoutout', { current: item?.Name, next: '' }, true));
        }
    } else if (item.IsNeutralZoneOut) {
        const idx = thisRaceRoles.findIndex((x) => x.ID == item.ID);
        if (idx > 0) {
            const previous = thisRaceRoles[idx - 1];
            if (!previous.IsNeutralZoneIn) {
                errors.push($ti('stationsetupwizard.neutralzoneoutwithoutin', { current: item?.Name, previous: previous?.Name }, true));
            }
        } else {
            errors.push($ti('stationsetupwizard.neutralzoneoutwithoutin', { current: item?.Name, previous: '' }, true));
        }
    }

    return errors;
};

/**
 *
 * This will make sure that start is before finish, and that we have only one start and one finish
 * If start is not the first, it will be a warning. If finish is not the last, it will be a warning (unless the last one is a backup for finish )
 * @param list
 * @returns
 */
const getStartErrors = (item: StationRole, list: StationRole[]): string[] => {
    const errors: string[] = [];

    const startIdx = list.filter((x) => x.RaceID == item.RaceID).findIndex((item) => item.Start);
    const finishIdx = list.filter((x) => x.RaceID == item.RaceID).findIndex((item) => item.Finish);

    if (finishIdx >= 0 && startIdx > finishIdx) {
        errors.push($ti('stationsetupwizard.startafterfinish', '', true));
    }

    return errors;
};

/**
 * This will make sure that start is before finish, and that we have only one start and one finish
 * If start is not the first, it will be a warning. If finish is not the last, it will be a warning (unless the last one is a backup for finish )
 * @param list
 */
const getStartWarnings = (item: StationRole, list: StationRole[]): string[] => {
    const warnings: string[] = [];

    const startIdx = list.filter((x) => x.RaceID == item.RaceID).findIndex((item) => item.Start);

    if (startIdx > 0) {
        warnings.push($tm('stationsetupwizard.startnotfirst', '', true));
    }
    return warnings;
};

/**
 * Will return errors if there are multiple finishes or no finish
 * @param item
 * @param list
 * @returns
 */
const getFinishErrors = (item: StationRole, list: StationRole[]): string[] => {
    const errors: string[] = [];

    // We need to make sure there is only one finish
    const finishIdsx = list.filter((x) => x.RaceID == item.RaceID && x.Finish);
    const selfIsLastInList = list.at(-1)?.ID == item.ID;
    if (finishIdsx.length > 1) {
        errors.push($ti('stationsetupwizard.multiplefinishes', '', true));
    } else if (finishIdsx.length == 0 && selfIsLastInList) {
        errors.push($ti('stationsetupwizard.nofinish', '', true));
    }

    return errors;
};
